import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Shield, MessageCircle, Trash2, MessageSquare, XCircle, BookOpen, Loader, Pencil, ShoppingCart } from 'lucide-react';
import { useListing, useDeleteListing, useUnlistItem, useCreateCheckout } from '@/hooks/useApi';
import { useAuthStore, useCartStore } from '@/store';
import SEO from '@/components/SEO';
import toast from 'react-hot-toast';
import { formatPrice } from '@/utils';
import { apiClient } from '@/lib/api-client';
import AuctionBidSection from '@/components/AuctionBidSection';
// import PriceComparison from '@/components/PriceComparison'; // TODO: re-enable when price sync goes live
import TradeOfferSection from '@/components/TradeOfferSection';
import UserAvatar from '@/components/UserAvatar';
import CardImage from '@/components/CardImage';
import ReportButton from '@/components/ReportButton';
import BlockUserButton from '@/components/BlockUserButton';

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: listing, isLoading, refetch } = useListing(id!);
  const { mutate: deleteListing, isLoading: deleting } = useDeleteListing();
  const { mutate: unlistItem, isLoading: unlisting } = useUnlistItem();
  const { mutate: createCheckout, isLoading: checkingOut } = useCreateCheckout();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const { addItem: addToCart, items: cartItems } = useCartStore();
  const isOwner = currentUser?.id === listing?.user_id;
  const canUnlist = isOwner && listing?.status === 'active';

  const [bids, setBids] = useState<any[]>([]);
  const [tradeOffers, setTradeOffers] = useState<any[]>([]);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [showOfferForm, setShowOfferForm] = useState(false);

  const isAuction = listing?.listing_type === 'auction';
  const isTrade = listing?.listing_type === 'trade';
  const isSale = listing?.listing_type === 'sale' || listing?.listing_type === 'both';

  const fetchBidsAndOffers = async () => {
    if (!id || !listing) return;
    try {
      const requests: Promise<any>[] = [];
      if (isAuction) requests.push(apiClient.get(`/listings/${id}/bids`));
      if (isTrade && currentUser) requests.push(apiClient.get(`/listings/${id}/trade-offers`));
      const results = await Promise.all(requests);
      if (isAuction) setBids(results[0]?.data?.data ?? results[0]?.data ?? []);
      if (isTrade && currentUser) setBids(results[isAuction ? 1 : 0]?.data?.data ?? []);
      if (isTrade && currentUser) setTradeOffers(results[isAuction ? 1 : 0]?.data?.data ?? results[isAuction ? 1 : 0]?.data ?? []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchBidsAndOffers(); }, [listing?.id]);

  const handleMessage = async () => {
    if (!currentUser) { navigate('/login'); return; }
    try {
      const response = await apiClient.post<any>('/conversations', {
        recipient_id: listing?.seller?.id,
        listing_id: listing?.id,
      });
      const convId = response.data?.conversation?.id;
      if (convId) navigate(`/messages/${convId}`);
    } catch (e) { console.error(e); }
  };

  const handleMakeOffer = async () => {
    if (!currentUser) { navigate('/login'); return; }
    try {
      await apiClient.post('/trade-offers', {
        listing_id: listing?.id,
        seller_id: listing?.seller?.id,
        offer_description: offerMessage,
        cash_included: offerAmount ? Number(offerAmount) : 0,
      });
      setShowOfferForm(false);
      setOfferAmount('');
      setOfferMessage('');
    } catch (e) { console.error(e); }
  };

  const images = listing?.image_urls && listing.image_urls.length > 0
    ? listing.image_urls
    : listing?.card?.image_url ? [listing.card.image_url] : [];

  const [selectedIdx, setSelectedIdx] = useState(0);
  const activeImage = images[selectedIdx] ?? images[0] ?? null;

  if (isLoading) {
    return (
      <div className="container-custom py-12">
        <div className="skeleton h-6 w-24 rounded mb-8" />
        <div className="grid md:grid-cols-2 gap-10">
          <div className="skeleton aspect-[3/4] rounded-2xl" />
          <div className="space-y-4">
            <div className="skeleton h-8 w-3/4 rounded" />
            <div className="skeleton h-40 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="container-custom py-24 text-center">
        <p className="text-5xl mb-4">🏷️</p>
        <h2 className="text-2xl font-bold mb-2">Listing not found</h2>
        <Link to="/marketplace" className="text-brand-600 dark:text-brand-400 hover:underline">Back to Marketplace</Link>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        title={listing.title}
        description={`${listing.title} — ${listing.condition ?? ''}${listing.price ? ` · ${listing.price}` : ''}. Buy trading cards on Open Card Vault.`}
        url={`/listings/${listing.id}`}
        image={listing.card?.image_url ?? undefined}
      />
      <div className="container-custom py-8">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:hover:text-brand-400 mb-6 font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to Marketplace
        </Link>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Card Image */}
          <div className="space-y-3">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-zinc-800 to-gray-200 max-w-xs mx-auto">
              <CardImage
                src={activeImage}
                alt={listing.card?.name ?? 'Card'}
                cardName={listing.card?.name}
                cardNumber={listing.card?.card_number}
                game={listing.card?.game}
                className="w-full h-full"
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 justify-center max-w-xs mx-auto flex-wrap">
                {images.map((url: string, i: number) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedIdx(i)}
                    className={`h-16 w-12 rounded-lg overflow-hidden border-2 transition-colors ${
                      i === selectedIdx
                        ? 'border-brand-500'
                        : 'border-gray-200 dark:border-zinc-700 hover:border-brand-400'
                    }`}
                  >
                    <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Listing Details */}
          <div>
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold mb-3 ${
              isAuction ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
              isTrade ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
              'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
            }`}>
              {isAuction && '🔨 Auction'}
              {isTrade && '🔄 Trade'}
              {isSale && <><Tag className="h-4 w-4" /> Buy Now</>}
            </div>

            <div className="flex items-start justify-between gap-3">
              <h1 className="text-3xl font-display font-bold mb-1">{listing.card?.name}</h1>
              {isOwner && (
                <div className="flex items-center gap-3 flex-shrink-0 mt-1">
                  <button
                    onClick={() => navigate(`/listings/${listing.id}/edit`)}
                    className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:hover:text-brand-400 font-medium transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to permanently delete this listing?')) {
                        deleteListing(listing.id, { onSuccess: () => navigate('/dashboard') });
                      }
                    }}
                    disabled={deleting}
                    className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500 hover:text-red-600 font-medium disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
            <p className="text-gray-500 dark:text-zinc-500 mb-4">
              {listing.card?.set_name} · Condition: <span className="font-medium text-gray-700 dark:text-zinc-300">{listing.condition}</span>
            </p>

            {/* Status banners */}
            {listing.status === 'cancelled' && (
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 rounded-xl px-4 py-3 mb-4 text-sm">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                <span>This listing has been removed from the marketplace. The card is still in your collection.</span>
                <Link to="/collection" className="ml-auto text-brand-600 dark:text-brand-400 font-medium hover:underline whitespace-nowrap">
                  View Collection →
                </Link>
              </div>
            )}
            {listing.status === 'sold' && (
              <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-blue-700 dark:text-blue-300 rounded-xl px-4 py-3 mb-4 text-sm">
                <Shield className="h-4 w-4 flex-shrink-0" />
                <span>This listing has been sold.</span>
              </div>
            )}

            {/* Owner: Remove from Sale button */}
            {canUnlist && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-2xl p-4 mb-4">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">You are the seller</p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                  Remove this card from the marketplace while keeping it in your collection. You can re-list it at any time.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (window.confirm('Remove this listing from the marketplace?\n\nThe card will stay in your collection and you can re-list it later.')) {
                        unlistItem(listing.id, {
                          onSuccess: () => {
                            toast.success('Listing removed from marketplace');
                            navigate('/collection');
                          },
                        });
                      }
                    }}
                    disabled={unlisting}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-zinc-900 border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    {unlisting ? 'Removing…' : 'Remove from Sale'}
                  </button>
                  <Link
                    to="/collection"
                    className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800 rounded-xl text-sm font-medium transition-colors"
                  >
                    <BookOpen className="h-4 w-4" />
                    View Collection
                  </Link>
                </div>
              </div>
            )}


            {/* Auction section */}
            {isAuction && listing.ends_at && (
              <AuctionBidSection
                listingId={listing.id}
                currentBid={listing.current_bid ?? listing.starting_price ?? 0}
                startingPrice={listing.starting_price ?? 0}
                endsAt={listing.ends_at}
                bids={bids}
                onBidPlaced={() => { fetchBidsAndOffers(); refetch(); }}
              />
            )}

            {/* Sale section */}
            {isSale && (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5 mb-5">
                <p className="text-sm text-gray-500 dark:text-zinc-500">Price</p>
                <p className="text-3xl font-bold text-brand-600 dark:text-brand-400 mb-4">{formatPrice(listing.price)}</p>
                <button
                  onClick={() => {
                    if (!currentUser) { navigate('/login'); return; }
                    createCheckout(listing.id, {
                      onSuccess: ({ url }) => { window.location.href = url; },
                      onError: () => toast.error('Could not start checkout. Please try again.'),
                    });
                  }}
                  disabled={checkingOut}
                  className="w-full py-3 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {checkingOut
                    ? <><Loader className="h-4 w-4 animate-spin" /> Redirecting to checkout…</>
                    : 'Buy Now'}
                </button>
                {!isOwner && (() => {
                  const inCart = cartItems.some((i) => i.listingId === listing.id);
                  return (
                    <button
                      onClick={() => {
                        if (!currentUser) { navigate('/login'); return; }
                        if (inCart) { navigate('/cart'); return; }
                        addToCart(listing.id);
                        toast.success('Added to cart');
                      }}
                      className="w-full mt-2 py-2.5 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-zinc-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      {inCart ? 'View Cart' : 'Add to Cart'}
                    </button>
                  );
                })()}
                {!isOwner && (
                  <>
                    <button
                      onClick={() => setShowOfferForm(!showOfferForm)}
                      className="w-full mt-2 py-2.5 border border-brand-300 text-brand-600 dark:text-brand-400 rounded-xl font-medium hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 transition-colors"
                    >
                      Make an Offer
                    </button>
                    {showOfferForm && (
                      <div className="mt-3 space-y-3 border-t pt-3">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500">$</span>
                          <input type="number" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} placeholder="Your offer amount" className="w-full pl-7 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400" />
                        </div>
                        <textarea value={offerMessage} onChange={(e) => setOfferMessage(e.target.value)} placeholder="Optional message..." rows={2} className="w-full border rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none text-sm" />
                        <div className="flex gap-2">
                          <button onClick={handleMakeOffer} disabled={!offerAmount} className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl font-semibold disabled:opacity-50">Send Offer</button>
                          <button onClick={() => setShowOfferForm(false)} className="px-4 py-2.5 border rounded-xl hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900">Cancel</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Trade section */}
            {isTrade && (
              <div className="mb-5">
                {listing.trade_for && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 mb-3">
                    <p className="text-sm text-purple-600 font-medium mb-1">Looking for:</p>
                    <p className="text-gray-800 dark:text-zinc-200">{listing.trade_for}</p>
                  </div>
                )}
                {!isOwner && (() => {
                  const inCart = cartItems.some((i) => i.listingId === listing.id);
                  return (
                    <button
                      onClick={() => {
                        if (!currentUser) { navigate('/login'); return; }
                        if (inCart) { navigate('/cart'); return; }
                        addToCart(listing.id);
                        toast.success('Saved to cart');
                      }}
                      className="w-full mb-3 py-2.5 border border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 rounded-xl font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      {inCart ? 'View in Cart' : 'Save to Cart'}
                    </button>
                  );
                })()}
                <TradeOfferSection
                  listingId={listing.id}
                  sellerId={listing.user_id}
                  tradeFor={listing.trade_for}
                  offers={tradeOffers}
                  onOfferPlaced={fetchBidsAndOffers}
                />
              </div>
            )}

            {/* Price Comparison — TODO: re-enable when price sync goes live */}
            {/* <div className="mb-5">
              <PriceComparison
                marketPrice={(listing.card as any)?.market_price}
                cardmarketPrice={(listing.card as any)?.cardmarket_price}
                foilPrice={(listing.card as any)?.foil_price}
                priceSource={(listing.card as any)?.price_source}
                lastPriceUpdate={(listing.card as any)?.last_price_update}
              />
            </div> */}

            {/* Trust badges */}
            <div className="flex gap-4 mb-5">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500">
                <Shield className="h-4 w-4 text-green-500" /><span>Buyer Protected</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500">
                <Shield className="h-4 w-4 text-blue-500" /><span>Secure Payment</span>
              </div>
            </div>

            {/* Forum discussion link */}
            {listing.card?.name && (
              <Link
                to={`/forum?search=${encodeURIComponent(listing.card.name)}`}
                className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors mb-4"
              >
                <MessageSquare className="h-4 w-4" />
                Discuss {listing.card.name} in the forum
              </Link>
            )}

            {/* Seller Info */}
            {listing.seller && (
              <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-4 flex items-center gap-4">
                <UserAvatar
                  username={listing.seller.username ?? '?'}
                  avatarUrl={listing.seller.avatar_url}
                  size="md"
                />
                <div className="flex-1">
                  <p className="font-semibold">{listing.seller.username}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-500">{listing.seller.total_sales ?? 0} sales · {listing.seller.rating ?? 'New seller'}</p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Link to={`/profile/${listing.seller.id}`} className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-medium">View Profile</Link>
                  {!isOwner && (
                    <button onClick={handleMessage} className="flex items-center gap-1 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:hover:text-brand-400 font-medium transition-colors">
                      <MessageCircle className="h-4 w-4" /> Message
                    </button>
                  )}
                  {!isOwner && (
                    <div className="flex gap-3 mt-1">
                      <ReportButton entityType="listing" entityId={listing.id} reportedUserId={listing.seller.id} />
                      <BlockUserButton userId={listing.seller.id} username={listing.seller.username} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}